
import React, { useState } from 'react'
import { useDrag } from 'react-dnd'
import { LIBRARY_MODEL_DRAG_TYPE } from './library-drag'
import {
  BrainCircuit,
  SlidersHorizontal,
  Image,
  Video,
  Volume2,
  Box,
  MessageSquareText,
  Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Combobox } from '@/components/ui/combobox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { AI_MODEL_CATEGORIES, AI_MODEL_CATEGORY_LABELS } from '@genieartinc/schemas'
import type { LibraryEntryWithModel } from '@genieartinc/schemas'
import { modelColor, modelSubtitle } from './library-types'
import type { CatalogModel, FilterPanelProps, NodeTypeFilter, SortOption } from './library-types'
import { SelectBox } from './LibraryBulkBar'

// ─── Toggle ───────────────────────────────────────────────────────────────────

export function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onChange() }}
      role="switch"
      aria-checked={checked}
      aria-label={checked ? 'Remove from library' : 'Add to library'}
      className={cn(
        'relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        checked ? 'bg-foreground' : 'bg-border hover:bg-muted-foreground/40',
      )}
    >
      <span
        className={cn(
          'inline-block h-3 w-3 transform rounded-full bg-background shadow-sm ring-0 transition-transform duration-200 mt-0.5',
          checked ? 'translate-x-3.5' : 'translate-x-0.5',
        )}
      />
    </button>
  )
}

// ─── Filter Panel Constants ──────────────────────────────────────────────────

const NODE_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'text-llm', label: 'Text' },
  { value: 'sound', label: 'Sound' },
  { value: '3d', label: '3D' },
]

const SORT_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'name-asc', label: 'Name A→Z' },
  { value: 'name-desc', label: 'Name Z→A' },
]

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  ...AI_MODEL_CATEGORIES.map((c) => ({ value: c, label: AI_MODEL_CATEGORY_LABELS[c] })),
]

// ─── Filter Panel ─────────────────────────────────────────────────────────────

export function FilterPanel({
  activeNodeType, category, modelFamily, sort, filterOptions,
  hasActiveFilters, onNodeTypeChange, onCategoryChange, onModelFamilyChange, onSortChange, onClear,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false)

  const familyOptions = [
    { value: 'all', label: 'All Families' },
    ...filterOptions.families.map((f) => ({ value: f, label: f })),
  ]

  const activeCount = [
    activeNodeType,
    category,
    modelFamily,
    sort !== 'featured' ? sort : '',
  ].filter(Boolean).length

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'relative flex h-9 w-9 items-center justify-center rounded-xl border border-border transition-colors flex-shrink-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            open ? 'bg-accent' : 'hover:bg-accent',
            hasActiveFilters && 'border-foreground/40',
          )}
          aria-label={`Filters${activeCount > 0 ? ` (${activeCount} active)` : ''}`}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
          {activeCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-background text-[10px] font-semibold leading-none">
              {activeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Filters</p>
            {hasActiveFilters && (
              <button
                onClick={() => { onClear(); setOpen(false) }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Type</p>
            <Combobox
              size="sm"
              options={NODE_TYPE_OPTIONS}
              value={activeNodeType || 'all'}
              onValueChange={(v) => onNodeTypeChange(v === 'all' ? '' : v as NodeTypeFilter)}
              placeholder="All Types"
              disableSearch
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Family</p>
            <Combobox
              size="sm"
              options={familyOptions}
              value={modelFamily || 'all'}
              onValueChange={(v) => onModelFamilyChange(v === 'all' ? '' : v)}
              placeholder="All Families"
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Category</p>
            <Combobox
              size="sm"
              options={CATEGORY_OPTIONS}
              value={category || 'all'}
              onValueChange={(v) => onCategoryChange(v === 'all' ? '' : v)}
              placeholder="All Categories"
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Sort</p>
            <Combobox
              size="sm"
              options={SORT_OPTIONS}
              value={sort}
              onValueChange={(v) => onSortChange((v || 'featured') as SortOption)}
              placeholder="Featured"
              disableSearch
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─── Node Type Icon ──────────────────────────────────────────────────────────

const NODE_TYPE_ICONS: Record<string, React.FC<{ className?: string }>> = {
  image: Image,
  video: Video,
  sound: Volume2,
  '3d': Box,
  'text-llm': MessageSquareText,
}

function NodeTypeIcon({ nodeType, className }: { nodeType?: string | null; className?: string }) {
  const IconComp = nodeType ? NODE_TYPE_ICONS[nodeType] : null
  return IconComp ? <IconComp className={className} /> : null
}

// ─── Select Checkbox ─────────────────────────────────────────────────────────

interface SelectCheckboxProps {
  name: string
  selected: boolean
  visible: boolean
  onSelect: () => void
  className?: string
}

function SelectCheckbox({ name, selected, visible, onSelect, className }: SelectCheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={`Select ${name}`}
      onClick={(e) => { e.stopPropagation(); onSelect() }}
      className={cn(
        'flex-shrink-0 rounded transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        visible || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
        className,
      )}
    >
      <SelectBox state={selected ? 'all' : 'none'} />
    </button>
  )
}

// ─── Community Card (Frosted Glass) ──────────────────────────────────────────

interface CommunityCardProps {
  onDragStart?: () => void
  model: CatalogModel
  isInLibrary: boolean
  onToggleLibrary: (modelId: string) => Promise<void>
  onAddToCanvas?: () => void
  onModelClick?: (model: CatalogModel) => void
  /** Bulk selection: checkbox state and toggle. `selecting` keeps the box visible without hover. */
  selected?: boolean
  selecting?: boolean
  onSelect?: () => void
}

export function CommunityCard({ model, isInLibrary, onToggleLibrary, onAddToCanvas, onModelClick, onDragStart, selected = false, selecting = false, onSelect }: CommunityCardProps) {
  const [, drag] = useDrag(() => ({ type: LIBRARY_MODEL_DRAG_TYPE, item: () => { onDragStart?.(); return { catalogModel: model } } }), [model, onDragStart])
  const bg = modelColor(model.id)
  const authorName = model.modelFamily ?? model.provider?.name ?? model.modelFamilyId ?? 'Author'

  // Build tag pills: purpose + nodeType/category + 1 capability (deduplicated)
  const tags: string[] = []
  if (model.purpose) tags.push(model.purpose)
  if (model.nodeType) tags.push(model.nodeType)
  else if (model.category) tags.push(model.category)
  if (model.capabilities?.[0] && tags.length < 2 && !tags.includes(model.capabilities[0])) tags.push(model.capabilities[0])

  return (
    <div ref={element => { drag(element) }} aria-label={`Model ${model.name}`}
      className={cn('relative isolate w-full aspect-[3/4] cursor-pointer group rounded-xl overflow-hidden', selected && 'ring-2 ring-foreground')}
      onClick={() => (selecting && onSelect ? onSelect() : onModelClick?.(model))}
    >
      {/* Background: gradient base + optional blurred photo */}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(135deg, ${bg.replace('92%', '35%')} 0%, ${bg.replace('92%', '18%')} 100%)` }}
      />
      {model.thumbnailUrl && !model.thumbnailUrl.endsWith('.svg') && (
        <>
          <img
            src={model.thumbnailUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover blur-2xl scale-125"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40" />
        </>
      )}

      {/* Content */}
      <div className="relative h-full flex flex-col justify-between p-3">
        {/* Top: family name, icon, model name */}
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            {onSelect && (
              <SelectCheckbox name={model.name} selected={selected} visible={selecting} onSelect={onSelect} className="mr-2" />
            )}
            <span className="text-[11px] font-medium text-white/70 truncate max-w-[75%] mr-auto">{authorName}</span>
            <NodeTypeIcon nodeType={model.nodeType} className="h-4 w-4 text-white/50 flex-shrink-0" />
          </div>
          <p className="text-sm font-bold text-white leading-snug">{model.name}</p>
        </div>

        {/* Bottom: badges + toggle */}
        <div className="flex items-end justify-between gap-2">
          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag, i) => (
                <span
                  key={`${tag}-${i}`}
                  className="inline-flex items-center h-5 px-2 rounded-md text-[11px] font-medium whitespace-nowrap bg-white/15 text-white/80"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : <div />}
          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Toggle checked={isInLibrary} onChange={() => void onToggleLibrary(model.id)} />
            {onAddToCanvas && (
              <button
                onClick={onAddToCanvas}
                className="flex items-center justify-center h-6 w-6 rounded-md bg-white/15 text-white/70 hover:bg-white/30 hover:text-white transition-colors"
                title="Open tool"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Compact Card (kept for potential future use) ─────────────────────────────

interface CompactCardProps {
  model: CatalogModel
  isInLibrary: boolean
  onToggleLibrary: (modelId: string) => Promise<void>
}

export function CompactCard({ model, isInLibrary, onToggleLibrary }: CompactCardProps) {
  return (
    <div className="text-left w-full">
      <div className="mt-2 flex items-start justify-between gap-1.5">
        <div className="min-w-0">
          <p className="text-xs font-medium truncate leading-tight">{model.name}</p>
          <p className="text-xs text-muted-foreground truncate">{modelSubtitle(model)}</p>
        </div>
        <Toggle checked={isInLibrary} onChange={() => void onToggleLibrary(model.id)} />
      </div>
    </div>
  )
}

// ─── Library Card ──────────────────────────────────────────────────────────────

interface LibraryCardProps {
  onDragStart?: () => void
  entry: LibraryEntryWithModel
  onToggleLibrary: (modelId: string) => Promise<void>
}

// ─── List Row ────────────────────────────────────────────────────────────────

interface ListRowProps {
  onDragStart?: () => void
  model: CatalogModel
  isInLibrary: boolean
  onToggleLibrary: (modelId: string) => Promise<void>
  onModelClick?: (model: CatalogModel) => void
  selected?: boolean
  selecting?: boolean
  onSelect?: () => void
}

export function ListRow({ model, isInLibrary, onToggleLibrary, onModelClick, onDragStart, selected = false, selecting = false, onSelect }: ListRowProps) {
  const [, drag] = useDrag(() => ({ type: LIBRARY_MODEL_DRAG_TYPE, item: () => { onDragStart?.(); return { catalogModel: model } } }), [model, onDragStart])
  const bg = modelColor(model.id)
  const authorName = model.modelFamily ?? model.provider?.name ?? model.modelFamilyId ?? 'Author'
  const price = model.pricing?.originalPrice
  const priceUnit = model.pricing?.unit

  return (
    <div ref={element => { drag(element) }} aria-label={`Model ${model.name}`}
      className={cn('flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/60 transition-colors cursor-pointer group', selected && 'bg-muted/60')}
      onClick={() => (selecting && onSelect ? onSelect() : onModelClick?.(model))}
    >
      {onSelect && <SelectCheckbox name={model.name} selected={selected} visible={selecting} onSelect={onSelect} />}

      {/* Color dot */}
      <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: bg }} />

      {/* Name + author */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate leading-tight">{model.name}</p>
        <p className="text-xs text-muted-foreground truncate">{authorName}</p>
      </div>

      {/* Tags */}
      <div className="hidden md:flex items-center gap-1 flex-shrink-0">
        {model.purpose && (
          <span className="inline-flex items-center bg-background border border-border h-5 px-1.5 rounded text-[11px] text-muted-foreground">
            {model.purpose}
          </span>
        )}
        {model.nodeType && (
          <span className="inline-flex items-center bg-background border border-border h-5 px-1.5 rounded text-[11px] text-muted-foreground">
            {model.nodeType}
          </span>
        )}
      </div>

      {/* Category */}
      <span className="text-xs text-muted-foreground w-28 truncate text-right flex-shrink-0 hidden lg:block">
        {model.category}
      </span>

      {/* Price */}
      <span className="text-xs text-muted-foreground w-20 text-right flex-shrink-0 tabular-nums">
        {price != null && price > 0
          ? `$${price < 0.01 ? price.toFixed(4) : price.toFixed(2)}`
          : '—'}
      </span>

      {/* Toggle */}
      <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
        <Toggle checked={isInLibrary} onChange={() => void onToggleLibrary(model.id)} />
      </div>
    </div>
  )
}

// ─── Library Card ──────────────────────────────────────────────────────────────

export function LibraryCard({ entry, onToggleLibrary, onDragStart }: LibraryCardProps) {
  const [, drag] = useDrag(() => ({ type: LIBRARY_MODEL_DRAG_TYPE, item: () => { onDragStart?.(); return { catalogModel: { ...entry.model, category: entry.model.providerCategory ?? '', featuredScore: 0, capabilities: [] } } } }), [entry, onDragStart])
  const bg = modelColor(entry.model.id)
  const authorName = entry.model.modelFamilyId ?? 'Author'

  const tags: string[] = []
  if (entry.model.purpose) tags.push(entry.model.purpose)
  if (entry.model.nodeType) tags.push(entry.model.nodeType)
  else if (entry.model.providerCategory) tags.push(entry.model.providerCategory)

  return (
    <div ref={element => { drag(element) }} aria-label={`Model ${entry.model.name}`} className="flex flex-col w-full cursor-pointer group rounded-lg bg-muted overflow-hidden">
      <div className="h-3 w-full" style={{ backgroundColor: bg }} />
      <div className="bg-muted p-2 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-base font-normal leading-6 text-foreground truncate">{entry.model.name}</p>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-0.5">
              {tags.map((tag, i) => (
                <span key={`${tag}-${i}`} className="inline-flex items-center justify-center bg-background border border-border h-6 px-2 rounded-lg text-xs text-foreground whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground truncate max-w-[120px]">{authorName}</span>
          <Toggle checked onChange={() => void onToggleLibrary(entry.model.id)} />
        </div>
      </div>
    </div>
  )
}
